
'use strict';

const React = require('react');
const PropTypes = require('prop-types');
const Relay = require('react-relay');

// Stores
const PriceStore = require('../../../global/stores/PriceStore.es.js');
const ItemStore = require('../../stores/ItemStore.es');
const PdpUiStore = require('../../stores/PdpUiStore.es');
const CalloutStore = require('../../../components/Callout/CalloutStore');
const TourStore = require('../../../components/Tour/TourStore');

// Components
const ContextComponent = require('../../../global/components/ContextComponent');
const { Price: PdpPrice } = require('../../../components/Price/Price');
const PdpFolderItem = require('../Favorites/PdpFolderItem').default;
const PdpFavoriteItem = require('../Favorites/PdpFavoriteItem').default;
const { Connected: Buttons } = require('./Buttons');
const Support = require('./Support');
const CustomHold = require('./CustomHold');
const ContactDealerSidebar = require('../ContactDealer/ContactDealerSidebar');
const { Connected: ProductionLeadTime } = require('../Shipping/ProductionLeadTime');
const Blurb = require('../Shipping/Blurb');
const { Connected: Title } = require('./Title');
const SaveSearch = require('../SaveSearch/SaveSearch');
const Benefits = require('./Benefits');
const TradeGuarantee = require('./TradeGuarantee');
const Urgency = require('./Urgency').default;
const ContactDealerBody = require('../ContactDealer/ContactDealerBody');

// Helpers
const provideContext = require('fluxible-addons-react/provideContext');
const connectToStores = require('fluxible-addons-react/connectToStores');
const SV = require('server-vars');
const tokenReplace = require('dibs-token-replace');
const get = require('lodash.get');
const classnames = require('classnames');
const priceHelpers = require('../../helpers/pdpPrice');
const pageTypes = require('../../../global/constants/pageTypes.es');
const tourTracking = require('../../../global/tracking/tourTracking');
const {type: { TRADE_TOUR }} = TourStore;

const abExperiments = require('../../../global/abExperiments.es');

const FavoriteModel = require('bunsen/models/identity/Favorite');
const TYPE_SEARCH = FavoriteModel.TYPE_SEARCH;
const FAVORITE_TRACKING = require('../../../components/FavoriteCta/trackingData');
const { variants } = require('../../constants/maytestConstants.es');

// Todo: Price status classes can go away when markup is in Twig as well as React see (see FINDING-7829)
// Add classes to apply min-heights to avoid Sidebar from contracting before all React components mount
const priceStatusToClassMap = {
    [priceHelpers.SOLD]: 'is-sold',
    [priceHelpers.HOLD]: 'is-hold',
    [priceHelpers.UPON_REQUEST]: 'is-upon-request',
    [priceHelpers.SHOW_PRICE]: 'is-show-price'
};

/**
 * quick and dirty to stop using pdpPrice module logic (still using constants)
 * @param isSold
 * @param isOnHold
 * @param isHidingPrice
 */
const getStatus = ({ isSold, isOnHold, showPriceUponRequest }) => {
    if (isSold) {
        return priceHelpers.SOLD;
    }
    if (isSold || isOnHold) {
        // On Hold, Recently Sold (i.e. not using sold template) flow
        return priceHelpers.HOLD;
    }
    if (showPriceUponRequest) {
        return priceHelpers.UPON_REQUEST;
    }
    return priceHelpers.SHOW_PRICE;
};

class Sidebar extends ContextComponent {
    constructor(props) {
        super(props);

        this.state = { isUrgencyVisible: false };

        this._contactDealer = this._contactDealer.bind(this);
    }

    componentDidMount() {
        if (abExperiments.pdpUrgency) {
            abExperiments.pdpUrgency.then(() => {
                this.setState({ isUrgencyVisible: true });
            });
        }
    }

    _contactDealer(e) {
        e.preventDefault();
        this.props.contactDealerClick(this.props);
    }

    render() {
        const props = this.props;
        const { buyerAssuranceProps,
            canMakePurchase,
            copy = {},
            customHold,
            dealerPk,
            hidePrice,
            isAsyncItemLoaded,
            isOnHold,
            isPreview,
            isSold,
            isUnlisted,
            itemId,
            priceDisplays,
            tradeTour,
            context,
            quotes,
            user = null,
            userId,
            viewer,
            relayItem,
            favoriteDealer,
            favoriteDealerCta,
            dealerData,
            showReducedFrom,
            pagePosition,
            callout,
            contactDealerBodyProps } = props;
        const priceStates = copy.priceStates;
        const priceStatus = getStatus({ isSold, isOnHold, showPriceUponRequest: priceDisplays.showPriceUponRequest });
        const expirationDate = priceDisplays.showOnHoldCustom && (customHold && customHold.expirationDate) ? customHold.expirationDate : null;
        const hasTradeBenefits = SV.get('data.hasPdpBenefits') && get(user, 'isVerifiedTrade');
        let buttonsEl;
        let requestInfoEl;
        let priceEl;
        let dealerEl;
        // Save Search
        const saveName = {name: SV.get('saveSearchData.name')};
        const favoriteData = {
            solrQuery: SV.get('saveSearchData.sq'),
            page: SV.get('saveSearchData.uri'),
            type: TYPE_SEARCH,
            authModalOptions: FAVORITE_TRACKING.SEARCH.SOLD_PDP_AUTH_MODAL
        };
        const { maytest } = contactDealerBodyProps || {};


        // FINDING-8230 Wait on displaying price to ensure converted currencies reflect latest conversion rates
        if ((isAsyncItemLoaded || isPreview)) {
            priceEl = (
                <PdpPrice context={context} pageType={pageTypes.PAGE_TYPE.PDP} showReducedFrom={showReducedFrom} showTour />
            );
        }

        // Show Save Search if sold item
        if (isSold || isUnlisted) {
            buttonsEl = (
                <SaveSearch
                    context={context}
                    description={tokenReplace(SV.get('favoriteCta.soldPdp.description'), saveName)}
                    cta={SV.get('favoriteCta.soldPdp.cta')}
                    successMessage={tokenReplace(SV.get('favoriteCta.soldPdp.confirmationTooltip'), saveName)}
                    favoriteData={favoriteData}
                    tracking={FAVORITE_TRACKING.SEARCH.SOLD_PDP}
                />
            );
            dealerEl = (
                <div className="pdp-sidebar-dealer-name">
                    {copy.dealer.offeredBy} <a href={dealerData.uri} title={dealerData.company}>{dealerData.company}</a>
                </div>
            );
        // Show shipping blurb if non-sold item is on hold (but could be on hold for a user) and is not sold
        } else if (!priceDisplays.showOnHold) {
            buttonsEl = (
                <Buttons context={context} user={user} />
            );
        }

        if (relayItem && (canMakePurchase || priceDisplays.showNetPrice) && !priceDisplays.showOnHold) {
            const isCustomizable = relayItem.isCustomizable;
            const copyText = isCustomizable ? copy.sidebar.customizeItem : copy.sidebar.requestInfo;

            requestInfoEl = (
                <ContactDealerSidebar
                    copy={copyText}
                    context={context}
                    onContactDealer={this._contactDealer}
                />
            );
        }

        const listingScore = get(relayItem, 'listingScore') || 0;
        const showUrgencyBox = (this.state.isUrgencyVisible && listingScore > 1);

        const sidebarClasses = classnames('pdp-sidebar', {
            'has-maytest-contact-dealer': maytest === variants.CONTACT2 || maytest === variants.CONTACT3,
            'has-trade-benefits': hasTradeBenefits
        });

        const priceClasses = classnames('pdp-price-container', {
            'has-net-price': priceDisplays.showNetPrice,
            'has-sale-price': priceDisplays.showSalePrice,
            'has-unavailable-price': priceDisplays.showSold || isUnlisted
        });

        return (
            <div className={`${sidebarClasses} ${priceStatusToClassMap[priceStatus]}`}>
                <div className="pdp-sidebar-box">
                    <div className="pdp-sidebar-top-section">
                        <div className="pdp-sidebar-top-favorite-item">
                            <PdpFavoriteItem
                                userId={userId}
                                itemId={itemId}
                                viewer={viewer}
                                callout={callout}
                            />
                            <PdpFolderItem
                                userId={userId}
                                itemId={itemId}
                                viewer={viewer}
                                callout={callout}
                            />
                        </div>
                        <Title context={context} />
                        {dealerEl}
                        <div className={priceClasses}>
                            {priceDisplays.showOnHold && <div className="is-on-hold">{priceStates.onHold}</div>}
                            {hasTradeBenefits && !priceDisplays.showPriceUponRequest ? <TradeGuarantee /> : null}
                            {priceEl}
                        </div>
                        <ProductionLeadTime relayItem={relayItem} />
                        <Blurb
                            pagePosition={`${pagePosition}-pdp-sidebar`}
                            userId={userId}
                            itemId={itemId}
                            dealerPk={dealerPk}
                            viewer={viewer}
                            context={context}
                            relayItem={relayItem}
                            quotes={quotes}
                            user={user}
                            favoriteDealer={favoriteDealer}
                            favoriteDealerCta={favoriteDealerCta}
                            dealerData={dealerData}
                        />
                        {priceDisplays.showOnHoldCustom
                            ? <CustomHold customHoldCopy={priceStates.onHoldForUser} expirationDate={expirationDate} />
                            : null}
                    </div>
                    <div className="pdp-sidebar-middle-section">
                        {buttonsEl}
                        {hasTradeBenefits && priceDisplays.showPriceUponRequest ? <TradeGuarantee moveUp /> : null}
                        {maytest !== variants.CONTACT2 && maytest !== variants.CONTACT3 && requestInfoEl}
                    </div>
                    {!hasTradeBenefits && !isSold && !isUnlisted ?
                        <div className="pdp-sidebar-bottom-section">
                            <Support
                                hidePrice={hidePrice}
                                tradeTour={tradeTour}
                                buyerAssuranceProps={buyerAssuranceProps}
                            />
                        </div>
                    : null}
                </div>

                {(maytest === variants.CONTACT2 || maytest === variants.CONTACT3) &&
                    <ContactDealerBody
                        {...contactDealerBodyProps}
                        sidebar
                        onContactDealerModal={this._contactDealer}
                    />
                }

                {showUrgencyBox && <Urgency views={listingScore} />}

                {hasTradeBenefits && <Benefits />}
            </div>
        );
    }
}

Sidebar.defaultProps = {
    showReducedFrom: true
};

Sidebar.propTypes = {
    buyerAssuranceProps: PropTypes.shape({
        copy: PropTypes.object.isRequired,
        state: PropTypes.shape({
            showInfo: PropTypes.bool.isRequired
        })
    }),
    canMakePurchase: PropTypes.bool,
    contactDealerClick: PropTypes.func.isRequired,
    context: PropTypes.object,
    copy: PropTypes.object.isRequired,
    customHold: PropTypes.object,
    dealerPk: PropTypes.string.isRequired,
    hidePrice: PropTypes.bool.isRequired,
    isAsyncItemLoaded: PropTypes.bool,
    isOnHold: PropTypes.bool.isRequired,
    isPreview: PropTypes.bool,
    isSold: PropTypes.bool.isRequired,
    isUnlisted: PropTypes.bool.isRequired,
    relayItem: PropTypes.object,
    itemId: PropTypes.string.isRequired,
    priceDisplays: PropTypes.object.isRequired,
    showReducedFrom: PropTypes.bool,
    tradeTour: PropTypes.object,
    userId: PropTypes.string,
    viewer: PropTypes.object,
    favoriteDealer: PropTypes.object,
    favoriteDealerCta: PropTypes.object,
    dealerData: PropTypes.object,
    pagePosition: PropTypes.string,
    callout: PropTypes.object,
    contactDealerBodyProps: PropTypes.object
};

module.exports = Relay.createContainer(provideContext(connectToStores(
    Sidebar,
    [
        ItemStore,
        PriceStore,
        PdpUiStore,
        TourStore,
        CalloutStore
    ],
    context => {
        const calloutStore = context.getStore(CalloutStore);
        const itemStore = context.getStore(ItemStore);
        const priceStore = context.getStore(PriceStore);
        const uiStore = context.getStore(PdpUiStore);
        const tourStore = context.getStore(TourStore);
        const item = itemStore.getItem();
        const processedPriceData = priceStore.processPrice(item);

        return ({
            buyerAssuranceProps: uiStore.getBuyerAssuranceProps(),
            canMakePurchase: itemStore.getCanMakePurchase() || false,
            copy: itemStore.getCopy(),
            customHold: itemStore.getFromItemModel('activeHold'),
            dealerPk: itemStore.getFromSV('data.dealerPk'),
            hidePrice: itemStore.getHidePrice(),
            isAsyncItemLoaded: itemStore.getIsAsyncItemLoaded(),
            isOnHold: itemStore.getIsOnHold(),
            isPreview: itemStore.getIsPreview(),
            isSold: itemStore.getIsSold(),
            isUnlisted: itemStore.getIsUnlisted(),
            itemId: itemStore.getFromSV('data.pk'),
            priceDisplays: processedPriceData.priceDisplays,
            tradeTour: {
                type: TRADE_TOUR,
                subType: processedPriceData.priceDisplays.showNetPrice ? 'preQuotedNetPrice' : 'requestNetPrice',
                copy: SV.get(`tourCopy.${TRADE_TOUR}`),
                isTourOpen: tourStore.isTourOpen(TRADE_TOUR),
                currentStep: tourStore.getCurrentStep(TRADE_TOUR),
                totalSteps: tourStore.getTotalSteps(TRADE_TOUR),
                onOpen: tourTracking.trackOpen,
                onClose: tourTracking.trackClose
            },
            callout: calloutStore.getCalloutData()
        });
    }
)), {
    initialVariables: {
        userId: null,
        itemId: null,
        hasUserId: false
    },
    fragments: {
        user: ({itemId, userId, hasUserId}) => Relay.QL`
            fragment on User {
                ${Blurb.getFragment('user', {itemId, userId})}
                ${Buttons.getFragment('user')}
                ${ContactDealerBody.getFragment('user', {userId, itemId, hasUserId})}
                isVerifiedTrade: hasPermission(name: "B_NET_PRICE")
            }
        `,
        viewer: ({ itemId, userId, hasUserId }) => Relay.QL`
            fragment on Viewer {
                ${PdpFavoriteItem.getFragment('viewer', {itemId, userId})}
                ${PdpFolderItem.getFragment('viewer', {itemId, userId})}
                ${Blurb.getFragment('viewer', {itemId, userId}).if(hasUserId)}
                ${ContactDealerBody.getFragment('viewer', {userId, itemId, hasUserId})}
            }
        `,
        relayItem: ({ itemId, userId, hasUserId }) => Relay.QL`
            fragment on Item {
                ${Blurb.getFragment('relayItem')}
                ${ProductionLeadTime.getFragment('relayItem')}
                ${ContactDealerBody.getFragment('item', {userId, itemId, hasUserId})}
                isCustomizable
                listingScore
            }
        `
    }
});



// WEBPACK FOOTER //
// ./assets/js/newPdp/components/Sidebar/Sidebar.jsx